import * as React from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { CheckCircle2, PackageCheck, Undo2, XCircle } from 'lucide-react';
import { z } from 'zod';

import { Card, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { DataTable, type DataColumn } from '@/components/data/DataTable';
import { DateTime } from '@/components/data/DateTime';
import { ShortCode } from '@/components/data/ShortCode';
import { StateMachineButtonGroup, type StateMachineTransition } from '@/components/data/StateMachineButtonGroup';
import { AuditChip } from '@/components/audit/AuditChip';
import { RejectReturnDialog } from '@/features/commerce-ops/RejectReturnDialog';
import { RETURN_STATES, returnStateTone } from '@/features/commerce-ops/orderState';
import {
  returnStateSchema,
  type ReturnListFilters,
  type ReturnRow,
  type ReturnState,
} from '@/schemas/admin/commerce/returns';
import {
  useApproveReturn,
  useReceiveReturn,
  useRefundReturn,
  useReturns,
} from '@/queries/commerceOps';
import { useApiError } from '@/lib/useApiError';

const searchSchema = z.object({
  state: returnStateSchema.optional(),
});

type ReturnsSearch = z.infer<typeof searchSchema>;

export const Route = createFileRoute('/_authed/commerce/returns')({
  validateSearch: searchSchema,
  component: ReturnsScreen,
});

function ReturnsScreen() {
  const { t } = useTranslation('commerce');
  const search = Route.useSearch();
  const navigate = useNavigate();
  const resolveError = useApiError();

  const filters: ReturnListFilters = React.useMemo(() => {
    const out: ReturnListFilters = { limit: 100 };
    if (search.state) out.state = search.state;
    return out;
  }, [search.state]);

  const list = useReturns(filters);
  const approve = useApproveReturn();
  const receive = useReceiveReturn();
  const refund = useRefundReturn();
  const [rejecting, setRejecting] = React.useState<string | null>(null);

  const data = list.data ?? [];

  function setSearchParam<K extends keyof ReturnsSearch>(key: K, value: ReturnsSearch[K]) {
    const next: ReturnsSearch = { ...search, [key]: value };
    void navigate({ to: '/commerce/returns', search: next, replace: true });
  }

  async function runMutation(label: string, fn: () => Promise<void>) {
    try {
      await fn();
      toast.success(label);
    } catch (err) {
      const resolved = resolveError(err);
      toast.error(
        resolved.message,
        resolved.recovery ? { description: resolved.recovery } : undefined
      );
    }
  }

  function transitionsFor(row: ReturnRow): StateMachineTransition<ReturnState>[] {
    return [
      {
        to: 'approved',
        label: t('returns.actions.approve'),
        from: ['requested'],
        icon: CheckCircle2,
        action: () => runMutation(t('returns.audit.approved'), () => approve.mutateAsync(row.id)),
      },
      {
        to: 'rejected',
        label: t('returns.actions.reject'),
        from: ['requested'],
        icon: XCircle,
        variant: 'secondary',
        action: () => {
          setRejecting(row.id);
        },
      },
      {
        to: 'received',
        label: t('returns.actions.receive'),
        from: ['approved'],
        icon: PackageCheck,
        action: () => runMutation(t('returns.audit.received'), () => receive.mutateAsync(row.id)),
      },
      {
        to: 'refunded',
        label: t('returns.actions.refund'),
        from: ['received'],
        icon: Undo2,
        variant: 'danger',
        disabledReason: t('returns.actions.refundHint'),
        action: () => runMutation(t('returns.audit.refunded'), () => refund.mutateAsync(row.id)),
      },
    ];
  }

  const columns: readonly DataColumn<ReturnRow>[] = [
    {
      id: 'order',
      header: t('returns.table.order'),
      cell: (r) =>
        r.order_short_code ? (
          <ShortCode code={r.order_short_code} to={`/commerce/orders/${r.order_id}`} />
        ) : (
          <span className="font-mono text-xs text-[color:var(--color-fg-muted)]">{r.order_id}</span>
        ),
    },
    {
      id: 'reason',
      header: t('returns.table.reason'),
      cell: (r) => (
        <span className="block max-w-md truncate text-sm">{r.reason ?? '—'}</span>
      ),
    },
    {
      id: 'state',
      header: t('returns.table.state'),
      cell: (r) => <Badge tone={returnStateTone(r.state)}>{t(`returns.states.${r.state}`)}</Badge>,
      sortBy: (r) => r.state,
    },
    {
      id: 'createdAt',
      header: t('returns.table.createdAt'),
      cell: (r) => <DateTime value={r.created_at ?? null} />,
      sortBy: (r) => r.created_at ?? '',
    },
    {
      id: 'resolvedAt',
      header: t('returns.table.resolvedAt'),
      cell: (r) => <DateTime value={r.resolved_at ?? null} />,
      sortBy: (r) => r.resolved_at ?? '',
    },
    {
      id: 'actions',
      header: t('returns.table.actions'),
      cell: (r) => (
        <StateMachineButtonGroup<ReturnState>
          currentState={r.state}
          transitions={transitionsFor(r)}
          hideInvalid
        />
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">{t('returns.title')}</h1>
          <p className="max-w-3xl text-sm text-[color:var(--color-fg-muted)]">{t('returns.subtitle')}</p>
        </div>
        <AuditChip />
      </header>

      <Card>
        <CardContent className="space-y-4 pt-6">
          <div className="flex flex-wrap items-end gap-3">
            <div className="basis-56">
              <label htmlFor="returns-state" className="sr-only">
                {t('returns.filters.stateAll')}
              </label>
              <select
                id="returns-state"
                value={search.state ?? ''}
                onChange={(e) =>
                  setSearchParam('state', (e.target.value || undefined) as ReturnState | undefined)
                }
                className="block h-11 w-full rounded-[var(--radius-md)] border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-accent)]"
              >
                <option value="">{t('returns.filters.stateAll')}</option>
                {RETURN_STATES.map((s) => (
                  <option key={s} value={s}>
                    {t(`returns.states.${s}`)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <DataTable<ReturnRow>
            data={data}
            columns={columns}
            rowKey={(r) => r.id}
            loading={list.isLoading}
            error={list.error ? resolveError(list.error) : null}
            empty={{
              title: t('returns.empty.title'),
              body: t('returns.empty.body'),
            }}
            initialSort={{ id: 'createdAt', direction: 'desc' }}
          />
        </CardContent>
      </Card>

      <RejectReturnDialog
        returnId={rejecting}
        open={Boolean(rejecting)}
        onOpenChange={(open) => {
          if (!open) setRejecting(null);
        }}
      />
    </div>
  );
}
